import { createContext, useState, useEffect, ReactNode, useRef } from 'react';
import { User } from 'firebase/auth';
import { subscribeToAuthChanges, getCurrentUser } from '@/auth/firebase';
import { usePostHog } from 'posthog-js/react';
import { setSentryUser, clearSentryUser } from '@/config/sentry';
import { useUserPreferences } from '@/stores/userPreferences';
import { useOnboardingStore } from '@/stores/onboarding';

/**
 * Shape of the user context value
 */
export interface UserContextValue {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
}

/**
 * Context for authenticated user state
 */
export const UserContext = createContext<UserContextValue | undefined>(undefined);

/**
 * Provider that manages auth subscription and shares user state
 */
export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(getCurrentUser());
  const [loading, setLoading] = useState(true);
  const posthog = usePostHog();
  const lastIdentifiedUserIdRef = useRef<string | null>(null);
  const { loadPreferences, clearPreferences } = useUserPreferences();
  const { registrationData } = useOnboardingStore();

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((authUser) => {
      setUser(authUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Identify user in PostHog and Sentry when authenticated; reset on logout
  // Also load user preferences when authenticated
  useEffect(() => {
    if (loading) return;

    if (user) {
      const currentId = user.uid;

      // Identify in PostHog (only if PostHog is available)
      if (posthog && lastIdentifiedUserIdRef.current !== currentId) {
        posthog.identify(currentId, {
          $email: user.email ?? undefined,
          $name: user.displayName ?? undefined,
          auth_provider: user.providerData[0]?.providerId,
        });
        lastIdentifiedUserIdRef.current = currentId;
      }

      // Identify in Sentry
      setSentryUser({
        id: currentId,
        email: user.email,
        username: user.displayName,
      });

      // Skip loading preferences if user is in pending social auth registration
      // (Firebase user exists but backend user record doesn't exist yet)
      const isPendingSocialRegistration =
        (registrationData.authMethod === 'google' || registrationData.authMethod === 'apple') && registrationData.registrationToken;

      if (!isPendingSocialRegistration) {
        // Load user preferences in background (non-blocking)
        loadPreferences().catch((error) => {
          console.error('Failed to load user preferences:', error);
        });
      }
    } else {
      // If user logs out, clear identification in both PostHog and Sentry
      if (posthog) {
        posthog.reset();
      }
      clearSentryUser();
      lastIdentifiedUserIdRef.current = null;
      clearPreferences();
    }
  }, [user, loading, posthog, loadPreferences, clearPreferences, registrationData]);

  const value: UserContextValue = {
    user,
    loading,
    isAuthenticated: !!user,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}; 